import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PersonIcon from '@material-ui/icons/Person';
import EventIcon from '@material-ui/icons/Event';
import ViewListIcon from '@material-ui/icons/ViewList';
import DonutLargeIcon from '@material-ui/icons/DonutLarge';
import { useApi, configApiRef, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { PocBox } from '../utils/PocBox';

interface WorkItem {
  id: number;
  title: string;
  state: string;
  reason?: string;
  assignedTo: { name: string; email: string | null } | null;
  createdDate: string;
  changedDate: string;
  priority?: number;
  url: string;
}

interface FeaturesResponse {
  active: WorkItem[];
  closed: WorkItem[];
}

// Left-border accent color + state badge colors
const STATE_STYLE: Record<string, { accent: string; bg: string; text: string; dot: string }> = {
  'New':             { accent: '#90A4AE', bg: '#ECEFF1', text: '#455A64', dot: '#90A4AE' },
  'Active':          { accent: '#1976D2', bg: '#E3F2FD', text: '#1565C0', dot: '#1976D2' },
  'In Progress':     { accent: '#1976D2', bg: '#E3F2FD', text: '#1565C0', dot: '#1976D2' },
  'Solution Design': { accent: '#F57C00', bg: '#FFF3E0', text: '#E65100', dot: '#F57C00' },
  'In Analysis':     { accent: '#7B1FA2', bg: '#F3E5F5', text: '#6A1B9A', dot: '#7B1FA2' },
  'Business Sign-Off':{ accent: '#00796B', bg: '#E0F2F1', text: '#004D40', dot: '#00796B' },
  'Blocked':         { accent: '#D32F2F', bg: '#FFEBEE', text: '#C62828', dot: '#D32F2F' },
  'Closed':          { accent: '#388E3C', bg: '#E8F5E9', text: '#2E7D32', dot: '#388E3C' },
  'Resolved':        { accent: '#388E3C', bg: '#E8F5E9', text: '#2E7D32', dot: '#388E3C' },
  'Done':            { accent: '#388E3C', bg: '#E8F5E9', text: '#2E7D32', dot: '#388E3C' },
  'Removed':         { accent: '#BDBDBD', bg: '#F5F5F5', text: '#9E9E9E', dot: '#BDBDBD' },
};

const DEFAULT_STATE_STYLE = { accent: '#BDBDBD', bg: '#F5F5F5', text: '#616161', dot: '#BDBDBD' };

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function teamsLink(email: string, title: string, id: number, url: string): string {
  const msg = `Hi, I'm following up on my feature request:\n"${title}" (Work Item #${id})\n${url}`;
  return `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}&message=${encodeURIComponent(msg)}`;
}

function WorkItemCard({ item }: { item: WorkItem }) {
  const s = STATE_STYLE[item.state] ?? DEFAULT_STATE_STYLE;
  const isBlocked = item.state === 'Blocked';

  return (
    <div style={{
      display: 'flex',
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 10,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      border: '1px solid #E8E8E8',
      background: '#fff',
      transition: 'box-shadow 0.15s',
    }}>
      <div style={{ flex: 1, padding: '12px 14px' }}>
        {/* Title row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: '#212121',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                lineHeight: 1.4,
              }}
            >
              {item.title}
              <OpenInNewIcon style={{ fontSize: 12, color: '#9E9E9E', flexShrink: 0 }} />
            </a>
            <div style={{ fontSize: 11, color: '#9E9E9E', marginTop: 1 }}>#{item.id}</div>
          </div>

          {/* State badge */}
          <span style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 9px',
            borderRadius: 20,
            background: s.accent+'20',
            color: s.text,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.3,
            whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
            {item.state}
          </span>
        </div>

        {/* Meta row */}
        <div style={{
          marginTop: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 20px',
          fontSize: 11.5,
          color: '#757575',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <PersonIcon style={{ fontSize: 13, color: '#BDBDBD' }} />
            {item.assignedTo
              ? item.assignedTo.email
                ? (
                  <a
                    href={teamsLink(item.assignedTo.email, item.title, item.id, item.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Chat in Microsoft Teams"
                    style={{ color: '#5B5FC7', textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                  >
                    {item.assignedTo.name}
                    <span style={{ fontSize: 10, background: '#EDEBFF', color: '#5B5FC7', borderRadius: 4, padding: '1px 4px', fontWeight: 700, letterSpacing: 0.2 }}>Teams</span>
                  </a>
                )
                : <span style={{ fontWeight: 500, color: '#424242' }}>{item.assignedTo.name}</span>
              : <em style={{ color: '#BDBDBD' }}>Unassigned</em>}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <EventIcon style={{ fontSize: 13, color: '#BDBDBD' }} />
            Updated {formatDate(item.changedDate)}
          </span>
          <span style={{ color: '#BDBDBD' }}>Created {formatDate(item.createdDate)}</span>
          {item.priority !== undefined && (
            <span style={{
              padding: '1px 7px',
              borderRadius: 10,
              background: '#F5F5F5',
              color: '#616161',
              fontWeight: 600,
              fontSize: 10.5,
            }}>
              P{item.priority}
            </span>
          )}
        </div>

        {/* Blocked banner */}
        {isBlocked && (
          <div style={{
            marginTop: 10,
            padding: '8px 10px',
            background: '#FFF5F5',
            borderRadius: 6,
            border: '1px solid #FFCDD2',
            fontSize: 11.5,
            color: '#B71C1C',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
            <span style={{ fontWeight: 700 }}>⛔ Blocked{item.reason ? `:` : ''}</span>
            {item.reason && <span style={{ color: '#C62828' }}>{item.reason}</span>}
            {item.assignedTo?.email && (
              <a
                href={teamsLink(item.assignedTo.email, item.title, item.id, item.url)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#5B5FC7', fontWeight: 600, fontSize: 11.5, textDecoration: 'none' }}
              >
                💬 Message assignee on Teams
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const PAGE_SIZE = 3;

function PaginationBar({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginTop: 14 }}>
      <button
        onClick={() => onPage(page - 1)}
        disabled={page === 0}
        style={{
          padding: '4px 12px', borderRadius: 6, border: '1.5px solid #E0E0E0',
          background: page === 0 ? '#FAFAFA' : '#fff', color: page === 0 ? '#BDBDBD' : '#424242',
          cursor: page === 0 ? 'default' : 'pointer', fontWeight: 600, fontSize: 13,
        }}
      >
        &laquo;
      </button>
      {Array.from({ length: totalPages }, (_, i) => (
        <button
          key={i}
          onClick={() => onPage(i)}
          style={{
            minWidth: 32, height: 32, borderRadius: 6,
            border: i === page ? '1.5px solid #1565C0' : '1.5px solid #E0E0E0',
            background: i === page ? '#1565C0' : '#fff',
            color: i === page ? '#fff' : '#424242',
            fontWeight: i === page ? 700 : 500, fontSize: 13, cursor: 'pointer',
          }}
        >
          {i + 1}
        </button>
      ))}
      <button
        onClick={() => onPage(page + 1)}
        disabled={page === totalPages - 1}
        style={{
          padding: '4px 12px', borderRadius: 6, border: '1.5px solid #E0E0E0',
          background: page === totalPages - 1 ? '#FAFAFA' : '#fff',
          color: page === totalPages - 1 ? '#BDBDBD' : '#424242',
          cursor: page === totalPages - 1 ? 'default' : 'pointer', fontWeight: 600, fontSize: 13,
        }}
      >
        &raquo;
      </button>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div style={{
      textAlign: 'center',
      padding: '36px 0',
      color: '#BDBDBD',
    }}>
      <Typography variant="body2" style={{ color: '#BDBDBD' }}>{label}</Typography>
    </div>
  );
}

// Graphical view — minimalist pie chart of request status distribution.
function StatusChartView({ items }: { items: WorkItem[] }) {
  const total = items.length;

  const counts = new Map<string, number>();
  items.forEach(i => counts.set(i.state, (counts.get(i.state) ?? 0) + 1));
  const entries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);

  if (total === 0) {
    return <EmptyState label="No items to visualize for the selected filters." />;
  }

  const size = 168;
  const r = size / 2;

  const point = (angle: number) => {
    const rad = (angle - 90) * (Math.PI / 180);
    return { x: r + r * Math.cos(rad), y: r + r * Math.sin(rad) };
  };

  let angleAcc = 0;
  const slices = entries.map(([state, count]) => {
    const s = STATE_STYLE[state] ?? DEFAULT_STATE_STYLE;
    const sweep = (count / total) * 360;
    const start = angleAcc;
    const end = angleAcc + sweep;
    angleAcc = end;
    return {
      state,
      count,
      pct: Math.round((count / total) * 100),
      color: s.dot,
      start,
      end,
      sweep,
    };
  });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 36, padding: '8px 0' }}>
      <svg width={size} height={size} style={{ flexShrink: 0, margin: '0 auto' }}>
        {slices.length === 1 ? (
          <circle cx={r} cy={r} r={r} fill={slices[0].color} />
        ) : (
          slices.map(sl => {
            const a = point(sl.start);
            const b = point(sl.end);
            const largeArc = sl.sweep > 180 ? 1 : 0;
            const d = `M ${r} ${r} L ${a.x} ${a.y} A ${r} ${r} 0 ${largeArc} 1 ${b.x} ${b.y} Z`;
            return <path key={sl.state} d={d} fill={sl.color} stroke="#fff" strokeWidth={2} />;
          })
        )}
      </svg>

      {/* Legend */}
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 12.5, color: '#9E9E9E', marginBottom: 14 }}>
          {total} request{total !== 1 ? 's' : ''}
        </div>
        {slices.map(sl => (
          <div key={sl.state} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: sl.color, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#424242', flex: 1 }}>{sl.state}</span>
            <span style={{ fontSize: 13, color: '#212121', fontWeight: 600 }}>{sl.count}</span>
            <span style={{ fontSize: 12.5, color: '#9E9E9E', minWidth: 42, textAlign: 'right' }}>{sl.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StepStatusTracking({ classes }: { classes: any }) {
  const config = useApi(configApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [tab, setTab] = useState(0);
  const [features, setFeatures] = useState<FeaturesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const hasFetchedRef = useRef(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterStates, setFilterStates] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'chart'>('list');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const profile = await identityApi.getProfileInfo();
      const email = profile.email;

      if (!email) {
        setError('Could not determine your email address. Please re-login.');
        return;
      }

      const resp = await fetchApi(
        `${backendUrl}/api/azdo-features/features?submitterEmail=${encodeURIComponent(email)}`,
      );

      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`Backend returned ${resp.status}: ${body}`);
      }

      const data: FeaturesResponse = await resp.json();
      setFeatures(data);
      setLastFetched(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, fetchApi, identityApi]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      load();
    } else if (refreshKey > 0) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const activeItems = features?.active ?? [];
  const closedItems = features?.closed ?? [];
  const allItems = [...activeItems, ...closedItems];

  const currentItems = tab === 0 ? activeItems : closedItems;
  const availableStates = Array.from(new Set(currentItems.map(i => i.state))).sort();

  const filteredItems = filterStates.length > 0
    ? currentItems.filter(i => filterStates.includes(i.state))
    : currentItems;

  const sortedItems = [...filteredItems].sort((a, b) => {
    const da = new Date(a.createdDate).getTime();
    const db = new Date(b.createdDate).getTime();
    return sortOrder === 'newest' ? db - da : da - db;
  });

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const pagedItems = sortedItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, gap: 8 }}>
        <Typography variant="body2" style={{ color: '#757575', lineHeight: 1.6 }}>
          Track your submitted feature requests on the Cosmos Azure DevOps board.
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {lastFetched && !loading && (
            <Typography variant="caption" style={{ color: '#BDBDBD', fontSize: 11 }}>
              {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          <Tooltip title={loading ? 'Loading…' : 'Refresh'}>
            <span>
              <IconButton
                size="small"
                onClick={() => setRefreshKey(k => k + 1)}
                disabled={loading}
                style={{
                  color: loading ? '#BDBDBD' : '#1565C0',
                  background: '#F0F4FF',
                  borderRadius: 8,
                  padding: 6,
                }}
              >
                {loading
                  ? <CircularProgress size={14} style={{ color: '#BDBDBD' }} />
                  : <RefreshIcon style={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {viewMode === 'list' && [
          { label: 'In Progress', count: activeItems.length, active: tab === 0, color: '#1565C0', bg: '#E3F2FD' },
          { label: 'Closed', count: closedItems.length, active: tab === 1, color: '#2E7D32', bg: '#E8F5E9' },
        ].map((t, i) => (
          <button
            key={i}
            onClick={() => { setTab(i); setPage(0); setFilterStates([]); }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '6px 14px',
              borderRadius: 8,
              border: t.active ? `1.5px solid ${t.color}` : '1.5px solid #E0E0E0',
              background: t.active ? t.bg : '#FAFAFA',
              color: t.active ? t.color : '#9E9E9E',
              fontWeight: t.active ? 700 : 500,
              fontSize: 13,
              cursor: 'pointer',
              transition: 'all 0.15s',
              outline: 'none',
            }}
          >
            {t.label}
            {features && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 20,
                height: 20,
                borderRadius: 10,
                background: t.active ? t.color : '#E0E0E0',
                color: t.active ? '#fff' : '#757575',
                fontSize: 10.5,
                fontWeight: 700,
                padding: '0 5px',
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}

        {/* List / Chart view toggle */}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', border: '1.5px solid #E0E0E0', borderRadius: 8, overflow: 'hidden' }}>
          {([
            { mode: 'list' as const, icon: <ViewListIcon style={{ fontSize: 16 }} />, label: 'List' },
            { mode: 'chart' as const, icon: <DonutLargeIcon style={{ fontSize: 16 }} />, label: 'Chart' },
          ]).map(v => {
            const active = viewMode === v.mode;
            return (
              <button
                key={v.mode}
                onClick={() => setViewMode(v.mode)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 12px', border: 'none', cursor: 'pointer',
                  background: active ? '#1565C0' : '#fff',
                  color: active ? '#fff' : '#757575',
                  fontWeight: active ? 700 : 500, fontSize: 12.5,
                }}
              >
                {v.icon}
                {v.label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Filter & Sort bar */}
      {features && !loading && viewMode === 'list' && availableStates.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <Typography style={{ fontSize: 11.5, fontWeight: 700, color: '#757575', marginRight: 2 }}>Filter:</Typography>
          {availableStates.map(state => {
            const s = STATE_STYLE[state] ?? DEFAULT_STATE_STYLE;
            const active = filterStates.includes(state);
            return (
              <button
                key={state}
                onClick={() => {
                  setPage(0);
                  setFilterStates(prev =>
                    active ? prev.filter(x => x !== state) : [...prev, state]
                  );
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 9px', borderRadius: 20, fontSize: 11.5,
                  fontWeight: active ? 700 : 500,
                  border: active ? `1.5px solid ${s.accent}` : '1.5px solid #E0E0E0',
                  background: active ? s.bg : '#FAFAFA',
                  color: active ? s.text : '#757575',
                  cursor: 'pointer',
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                {state}
              </button>
            );
          })}
          {filterStates.length > 0 && (
            <button
              onClick={() => { setFilterStates([]); setPage(0); }}
              style={{
                padding: '3px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 500,
                border: '1.5px solid #FFCDD2', background: '#FFF5F5', color: '#B00020', cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Typography style={{ fontSize: 11.5, fontWeight: 700, color: '#757575' }}>Sort:</Typography>
            {(['newest', 'oldest'] as const).map(o => (
              <button
                key={o}
                onClick={() => { setSortOrder(o); setPage(0); }}
                style={{
                  padding: '3px 9px', borderRadius: 20, fontSize: 11.5,
                  fontWeight: sortOrder === o ? 700 : 500,
                  border: sortOrder === o ? '1.5px solid #1565C0' : '1.5px solid #E0E0E0',
                  background: sortOrder === o ? '#E3F2FD' : '#FAFAFA',
                  color: sortOrder === o ? '#1565C0' : '#757575',
                  cursor: 'pointer',
                }}
              >
                {o === 'newest' ? 'Newest first' : 'Oldest first'}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Initial loading */}
      {loading && !features && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '28px 0', color: '#9E9E9E' }}>
          <CircularProgress size={18} style={{ color: '#1565C0' }} />
          <Typography variant="body2" style={{ color: '#9E9E9E' }}>Fetching your features from AZDO…</Typography>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{
          padding: '10px 14px',
          background: '#FFF8F0',
          borderRadius: 8,
          border: '1px solid #FFB74D',
          marginBottom: 14,
          fontSize: 13,
          color: '#E65100',
        }}>
          ⚠ {error}
        </div>
      )}

      {/* Content */}
      {!loading && !error && features && (
        <div>
          {viewMode === 'list' && filteredItems.length !== currentItems.length && currentItems.length > 0 && (
            <Typography style={{ fontSize: 11.5, color: '#9E9E9E', marginBottom: 10 }}>
              Showing {filteredItems.length} of {currentItems.length} items
            </Typography>
          )}
          {viewMode === 'chart' ? (
            <StatusChartView items={allItems} />
          ) : (
            <>
              {pagedItems.length === 0
                ? <EmptyState label={
                    currentItems.length === 0
                      ? `No ${tab === 0 ? 'active' : 'closed'} features found for your account.`
                      : 'No items match the selected filters.'
                  } />
                : pagedItems.map(item => <WorkItemCard key={item.id} item={item} />)
              }
              <PaginationBar page={page} totalPages={totalPages} onPage={setPage} />
            </>
          )}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <PocBox
          classes={classes}
          contact="Service Adoption Team"
          email="Service_Adoption_Team_Cosmos@cat.com"
          issues={[
            'Features not showing up',
            'Status not updated after 5 days',
            'Blocked item with no reason provided',
            'Filled Wrong Information in APIGEE form',
          ]}
        />
      </div>
    </div>
  );
}
